import { FC, ReactNode, useEffect, useState } from "react";
import { Form, FormControlProps, InputGroup } from "react-bootstrap";
import RequiredField from "./required-field";
import * as Yup from "yup";

interface GeneralProps {
  id?: string;
  fix?: string;
  children: ReactNode;
}

const Prefixed: FC<GeneralProps> = ({ children, id, fix }) => {
  return (
    <InputGroup className="mb-3">
      <InputGroup.Text id={`${id}-prefix`}>{fix}</InputGroup.Text>
      {children}
    </InputGroup>
  );
};

const Suffixed: FC<GeneralProps> = ({ children, id, fix }) => {
  return (
    <InputGroup className="mb-3">
      {children}
      <InputGroup.Text id={`${id}-suffix`}>{fix}</InputGroup.Text>
    </InputGroup>
  );
};

interface SimpleTextboxProps extends FormControlProps {
  prefix?: string;
  suffix?: string;
  label?: string;
  error?: string;
  hidden?: boolean;
  validationSchema?:
    | Yup.StringSchema
    | Yup.NumberSchema
    | Yup.ObjectSchema<any>;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const SimpleTextbox: FC<SimpleTextboxProps> = ({
  prefix,
  suffix,
  label,
  error,
  validationSchema,
  hidden = false,
  onChange,
  onBlur,
  ...props
}) => {
  //const [isInvalid, setIsInvalid] = useState(false);
  const [validationMessage, setValidationMessage] = useState<string>(error);

  useEffect(() => {
    if (error) {
      setValidationMessage(error);
    }
  }, [error]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (validationSchema) {
      try {
        if (props.type === "number") {
          console.log(
            "Validating number input: ",
            event.target.value === "" ? 0 : event.target.value
          );
          validationSchema.validateSync(
            event.target.value === "" ? 0 : event.target.value.trim()
          );
        } else {
          validationSchema.validateSync(event.target.value.trim());
        }

        setValidationMessage(null);
      } catch (error) {
        setValidationMessage(error.message);
      }
    }

    onChange?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!props.required && event.target.value.trim().localeCompare("") === 0) {
      setValidationMessage(null);
    }

    onBlur?.(event);
  };

  if (prefix) {
    return (
      <Form.Group className="form-group">
        {label && (
          <Form.Label>
            {label} {props.required && <RequiredField />}
          </Form.Label>
        )}
        <Prefixed id={props.id} fix={prefix}>
          <Form.Control
            {...props}
            isInvalid={!!validationMessage}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Prefixed>
        <Form.Control.Feedback type="invalid">
          {validationMessage}
        </Form.Control.Feedback>
      </Form.Group>
    );
  }

  if (suffix) {
    return (
      <Form.Group className="form-group">
        <Form.Label>
          {label ?? "Text"} {props.required && <RequiredField />}
        </Form.Label>
        <Suffixed id={props.id} fix={suffix}>
          <Form.Control
            {...props}
            isInvalid={!!validationMessage}
            onChange={handleChange}
            onBlur={handleBlur}
          />
        </Suffixed>
        <Form.Control.Feedback type="invalid">
          {validationMessage}
        </Form.Control.Feedback>
      </Form.Group>
    );
  }

  return (
    <Form.Group className={`form-group w-100 ${hidden ? "d-none" : ""}`}>
      <Form.Label>
        {label ?? "Text"} {props.required && <RequiredField />}
      </Form.Label>
      <Form.Control
        {...props}
        isInvalid={!!validationMessage}
        onChange={handleChange}
        onBlur={handleBlur}
        onWheel={(e) => e.currentTarget.blur()}
      />
      <Form.Control.Feedback type="invalid">
        {validationMessage}
      </Form.Control.Feedback>
    </Form.Group>
  );
};

export default SimpleTextbox;
